import { supabase } from '@/lib';
import { dbError, logger } from '@/lib/logger';
import type { ActivityLogChanges as StoredActivityLogChanges } from '@/types/activity-log';

export interface ActivityLogChanges {
  [key: string]: {
    from: unknown;
    to: unknown;
  };
}

export interface ActivityLogMetadata {
  [key: string]: unknown;
}

export function getObjectDiff(oldObj: Record<string, unknown> | null | undefined, newObj: Record<string, unknown> | null | undefined): ActivityLogChanges {
  const changes: ActivityLogChanges = {};
  
  // Handle null/undefined objects
  if (!oldObj || !newObj) {
    logger.warn('getObjectDiff called with null/undefined object', { oldObj: !!oldObj, newObj: !!newObj }, 'ActivityLogger');
    return changes;
  }
  
  // Check if we're receiving objects that already contain "new" and "old" keys
  // This would indicate duplicate/invalid activity logging calls
  if (('new' in oldObj && 'old' in oldObj) || ('new' in newObj && 'old' in newObj)) {
    logger.warn('getObjectDiff received objects with "new"/"old" keys - possible duplicate logging', { hasOldStructure: 'new' in oldObj, hasNewStructure: 'new' in newObj }, 'ActivityLogger');
    return changes; // Return empty changes to prevent creating "unknown" entries
  }
  
  // Helper function to normalize values for comparison
  const normalizeValue = (value: unknown): unknown => {
    // Treat empty strings and null as equivalent for optional fields
    if (value === '' || value === null || value === undefined) {
      return null;
    }
    
    // Convert dates to ISO strings for consistent comparison
    if (value instanceof Date) {
      return value.toISOString();
    }
    
    // Convert date strings to consistent format
    if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return new Date(value).toISOString().split('T')[0];
    }
    
    return value;
  };
  
  // Get all unique keys from both objects, excluding metadata fields
  const allKeys = [...new Set([...Object.keys(oldObj), ...Object.keys(newObj)])]
    .filter(key => !['updated_at', 'created_at', 'id'].includes(key)); // Exclude timestamp and ID fields
  
  for (const key of allKeys) {
    const oldValue = normalizeValue(oldObj[key]);
    const newValue = normalizeValue(newObj[key]);
    
    // Only log if normalized values are actually different
    if (oldValue !== newValue) {
      changes[key] = {
        from: oldValue,
        to: newValue
      };
    }
  }
  
  return changes;
}

export async function logActivity(
  action: string,
  entityType: string,
  entityId: string,
  // Diffs are `{from, to}`; a flat scalar records a value with no before/after
  // (e.g. a sync error) and renders as a single line in the activity log.
  changes: StoredActivityLogChanges = {},
  metadata: ActivityLogMetadata = {},
  memberId?: string
) {
  try {
    // Get current user session
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.user?.id) {
      dbError('No authenticated user found for activity logging', null, { action, entityType, entityId });
      return;
    }

    // Check if user is an admin first
    const { data: adminUser } = await supabase
      .from('admin_users')
      .select('full_name, email')
      .eq('id', session.user.id)
      .single();

    let userFullName = 'Unknown User';
    let userEmail = 'No email';
    let isAdmin = false;

    if (adminUser) {
      // User is an admin
      userFullName = adminUser.full_name;
      userEmail = adminUser.email;
      isAdmin = true;
    } else {
      // Check if user is a member
      const { data: member } = await supabase
        .from('members')
        .select('first_name, last_name, email')
        .eq('auth_user_id', session.user.id)
        .single();

      if (member) {
        userFullName = `${member.first_name} ${member.last_name}`;
        userEmail = member.email;
        isAdmin = false;
      } else {
        // User exists in auth but not in admin_users or members
        dbError('User not found in admin_users or members table', null, { 
          action, 
          entityType, 
          entityId, 
          userId: session.user.id 
        });
        return;
      }
    }

    // Prepare activity log entry
    const activityLogEntry = {
      action,
      entity_type: entityType,
      entity_id: entityId,
      changes: Object.keys(changes).length > 0 ? changes : null,
      metadata: Object.keys(metadata).length > 0 ? metadata : null,
      user_id: session.user.id,
      user_full_name: userFullName,
      user_email: userEmail,
      user_is_admin: isAdmin,
      member_id: memberId || null
    };

    // Insert activity log
    const { error: insertError } = await supabase
      .from('activity_logs')
      .insert(activityLogEntry);

    if (insertError) {
      dbError('Failed to insert activity log', insertError, { action, entityType, entityId });
    }
  } catch (error) {
    dbError('Unexpected error in logActivity', error, { action, entityType, entityId });
  }
}
